import {
  getAccessLevelDescription,
  AccessLevelDisplayNames,
  AccessLevelEntityType,
} from "@components/access-level/access-level-utils";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { useGetBulkDialogContent } from "@components/common/faro-table/bulk-dialog/bulk-dialog-helper";
import { BulkDialogs } from "@components/common/faro-table/bulk-dialog/bulk-dialog-types";
import { Alert, Text } from "@flat-ui";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useAppSelector } from "@store/store-helper";
import {
  bulkActionResultsSelector,
  numberOfUpdatingItemsSelector,
} from "@store/table/table-selector";
import { nounPluralize } from "@utils/data-display";

/** Determines the content of bulk dialog and bulk success dialog for changing access level */
export function useChangeAccessLevelBulkDialogContent(
  type: AccessLevelEntityType,
  accessLevel: FaroCoreApiTypes.ProjectAccessLevel
): BulkDialogs {
  const numberOfUpdatingItems = useAppSelector(numberOfUpdatingItemsSelector);
  const { numberOfSuccess, numberOfErrors } = useAppSelector(
    bulkActionResultsSelector
  );

  const bulkDialogContent = useGetBulkDialogContent({
    initial: {
      title: `Change access level for these ${type}s?`,
      description: (
        <>
          <Text variant="bodyM">
            When you confirm this action, the access level of the selected
            {type}s will be changed to{" "}
            <b>{AccessLevelDisplayNames[accessLevel]}</b>.
          </Text>

          <Alert
            variant="info"
            title={getAccessLevelDescription(type, accessLevel)}
            sx={{ mt: SPACE_ELEMENTS_OF_MODAL }}
          />
        </>
      ),
    },

    failed: {
      title: `Couldn't change the access level of ${numberOfErrors} out of ${numberOfUpdatingItems} ${type}s`,
      description: `The access level of these ${type}s couldn't be changed, check the details and try again.`,
    },
    confirmButtonText: "change",
    entityType: type,
  });

  const successDialogContent = {
    title: `${nounPluralize({
      counter: numberOfSuccess,
      word: type,
    })} changed access level.`,
    description: `The access level of all selected ${type}s was successfully changed.`,
  };

  return { bulkDialogContent, successDialogContent };
}
